import { useStoreState } from "pullstate";
import Store from "../../utils/Store";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Toast from "../Toast";
import { useTranslation } from "react-i18next";
import constants from "../../utils/constants";
import useUnregisterNotification from "./services/unregisterService";
import { getIsOnpremise, isIOSWebView } from "../../utils";
import { trackEvent } from "../../utils/mixpanel";
import mixpanel from "mixpanel-browser";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import getEnv from "../../utils/getEnv";
import { useAuth, useLogoutService } from "@qure/qure-auth";
import { useRouter } from "next/router";
import Countly from "countly-sdk-web";

const Logout = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { refetchProfile } = useAuth();
  const { isVisible } = useStoreState(Store, (s) => ({ isVisible: s.isLogoutOpen }));
  const logoutService = useLogoutService();
  const unregisterNotification = useUnregisterNotification();
  const { fcmToken, RNfcmToken } = useStoreState(Store, (s) => ({
    RNfcmToken: s.RNfcmToken,
    fcmToken: s.fcmToken,
  }));

  const onCloseLogout = () => {
    Store.update((s) => {
      s.isLogoutOpen = false;
    });
  };

  const postLogout = async () => {
    // desktop app integration
    if (window.electronAPI) {
      window.electronAPI.setAPISettings({
        apiURL: null,
        token: null,
      });
    }
    // delete shadow cookie created for iOS webview
    if (isIOSWebView()) {
      let now = new Date();
      let year = now.getFullYear();
      now.setFullYear(year - 1);
      document.cookie = `sessionid=;Domain=.qure.ai;Expires=${now.toUTCString()};Path=/;Secure;`;
    }

    // localStorage cleanup
    localStorage?.removeItem(constants.PERSISTED.fcmToken);
    localStorage?.removeItem(constants.PERSISTED.filterObj);
    localStorage?.removeItem(constants.PERSISTED.savedFilterData);
    localStorage?.removeItem(constants.PERSISTED.isFullWidthWorklist);
    localStorage?.removeItem(constants.PERSISTED.isSwiftReportModeEnabled);
    localStorage?.removeItem(constants.PERSISTED.notificationPromptDismiss);
    localStorage?.removeItem(constants.PERSISTED.requestedWorkspaceData);
    localStorage?.removeItem(constants.PERSISTED.mixpanelEvents);

    // Countly logout handling
    try {
      Countly.end_session(5);
      Countly.user_details({});
      // Optionally, reset device ID to anonymous
      Countly.change_id("ANONYMOUS_USER_ID", false);
    } catch (e) {
      // Ignore Countly errors on logout
    }

    // reset mixpanel identity
    if (!!getEnv("NEXT_PUBLIC_MIXPANEL_TOKEN")) {
      mixpanel.reset();
    }
    await refetchProfile();
    onCloseLogout();
  };

  const onLogout = async () => {
    try {
      const old_fcmToken = window?.localStorage.getItem(constants.PERSISTED.fcmToken);
      if (RNfcmToken || fcmToken || old_fcmToken) {
        await unregisterNotification.mutateAsync(RNfcmToken || fcmToken || old_fcmToken);
      }
      if (getIsOnpremise() && Countly?.add_event) {
        Countly.add_event({
          key: MIXPANEL_EVENTS.USER_LOGGEDOUT,
        });
      } else {
        trackEvent(MIXPANEL_EVENTS.USER_LOGGEDOUT);
      }

      await logoutService.mutateAsync();
      postLogout();
    } catch (error) {
      if (error?.response?.status === 401) {
        postLogout();
      } else {
        Toast.error(t("toast.something_went_wrong"));
      }
    }
  };

  return (
    <Modal
      show={isVisible}
      onClose={onCloseLogout}
      title={t("logout_modal.confirm_logout")}
      className="w-full sm:w-5/12 lg:w-4/12"
      closeOnClickAway
    >
      <div className="p-6">{t("logout_modal.logout_confirmation_message")}</div>
      <div className="flex items-center justify-end sm:justify-end p-4 w-full">
        <div className="flex items-center justify-center w-full sm:w-3/4 gap-4">
          <Button isSecondary className="grow w-full sm:w-auto" onClick={onCloseLogout}>
            {t("button.cancel")}
          </Button>
          <Button
            isPrimary
            isLoading={logoutService.isLoading || unregisterNotification.isLoading}
            className="mt-0 grow w-full sm:w-auto"
            onClick={onLogout}
            data-testid="logout-button"
          >
            {t("logout_key")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default Logout;
